import gql from 'graphql-tag';

export const GET_UPS_API_METRICS = gql`
  query InternalUPSApiMetrics(
    $startDate: String!
    $endDate: String!
    $operations: [String!]
    $bucketSizeMinutes: Int
  ) {
    internalUPSApiMetrics(
      startDate: $startDate
      endDate: $endDate
      operations: $operations
      bucketSizeMinutes: $bucketSizeMinutes
    ) {
      buckets {
        timestamp
        totalRequests
        successCount
        failureCount
        avgDurationMs
        rateLimitHits
        queuedCount
      }
      byOperation {
        operation
        count
        successRate
        avgDurationMs
      }
      liveStatus {
        production {
          queueDepth
          oldestJobAge
          rateLimitState {
            rate
            inventoryCount
          }
          legacyQueueDepth
        }
        staging {
          queueDepth
          oldestJobAge
          rateLimitState {
            rate
            inventoryCount
          }
          legacyQueueDepth
        }
        maxRate
        reservedForScan
      }
      recentRequests {
        id
        operation
        isStaging
        statusCode
        success
        durationMs
        errorCode
        errorMessage
        wasQueued
        createdAt
      }
      recentErrors {
        operation
        errorCode
        errorMessage
        statusCode
        count
        lastOccurred
      }
      totalRequests
      totalErrors
      totalRateLimitHits
      p95DurationMs
      p99DurationMs
    }
  }
`;

export interface MetricBucket {
  timestamp: string;
  totalRequests: number;
  successCount: number;
  failureCount: number;
  avgDurationMs: number;
  rateLimitHits: number;
  queuedCount: number;
}

export interface OperationBreakdown {
  operation: string;
  count: number;
  successRate: number;
  avgDurationMs: number;
}

export interface RateLimitState {
  rate: number;
  inventoryCount: number;
}

export interface QueueStats {
  queueDepth: number;
  oldestJobAge: number | null;
  rateLimitState: RateLimitState;
  legacyQueueDepth: number;
}

export interface LiveStatus {
  production: QueueStats;
  staging: QueueStats;
  maxRate: number;
  reservedForScan: number;
}

export interface RecentRequest {
  id: string;
  operation: string;
  isStaging: boolean;
  statusCode: number | null;
  success: boolean;
  durationMs: number;
  errorCode: string | null;
  errorMessage: string | null;
  wasQueued: boolean;
  createdAt: string;
}

export interface ErrorGroup {
  operation: string;
  errorCode: string | null;
  errorMessage: string | null;
  statusCode: number | null;
  count: number;
  lastOccurred: string;
}

export interface UpsApiMetricsResponse {
  buckets: MetricBucket[];
  byOperation: OperationBreakdown[];
  liveStatus: LiveStatus;
  recentRequests: RecentRequest[];
  recentErrors: ErrorGroup[];
  totalRequests: number;
  totalErrors: number;
  totalRateLimitHits: number;
  p95DurationMs: number;
  p99DurationMs: number;
}

export interface GetUpsApiMetricsData {
  internalUPSApiMetrics: UpsApiMetricsResponse;
}

export interface GetUpsApiMetricsVars {
  startDate: string;
  endDate: string;
  operations?: string[];
  bucketSizeMinutes?: number;
}

export const UPS_OPERATIONS = [
  'getPackageVisibility',
  'getAccessPoint',
  'searchPackage',
  'sendScanEventConsigneePickup',
  'sendScanEventShipperDropOff',
  'sendScanEvent',
  'updateAccessPoint',
  'sendReceiptEmail',
  'sendReceiptSMS',
  'getTrackingNumberFromEatn',
  'checkCommercialInvoice',
];

export const OPERATION_LABELS: Record<string, string> = {
  getPackageVisibility: 'Package Visibility',
  getAccessPoint: 'Get Access Point',
  searchPackage: 'Search Package',
  sendScanEventConsigneePickup: 'Consignee Pickup',
  sendScanEventShipperDropOff: 'Shipper Drop-Off',
  sendScanEvent: 'Scan Event',
  updateAccessPoint: 'Update Access Point',
  sendReceiptEmail: 'Receipt Email',
  sendReceiptSMS: 'Receipt SMS',
  getTrackingNumberFromEatn: 'EATN Tracking',
  checkCommercialInvoice: 'Commercial Invoice',
};

export const ENDPOINT_GROUPS: Record<string, { label: string; operations: string[] }> = {
  inventory: {
    label: 'Inventory',
    operations: ['getPackageVisibility'],
  },
  scanning: {
    label: 'Scanning',
    operations: ['sendScanEvent', 'sendScanEventConsigneePickup', 'sendScanEventShipperDropOff'],
  },
  accessPoint: {
    label: 'Access Point',
    operations: ['getAccessPoint', 'updateAccessPoint'],
  },
  lookup: {
    label: 'Lookup',
    operations: ['searchPackage', 'getTrackingNumberFromEatn', 'checkCommercialInvoice'],
  },
  notifications: {
    label: 'Notifications',
    operations: ['sendReceiptEmail', 'sendReceiptSMS'],
  },
};
